/**
 * The public trust page.
 *
 * Read by a stranger evaluating the company, so it carries no session, no
 * navigation into the app, and nothing that needs explaining. Every card shows
 * the date it was last reviewed, which is the part that makes it worth reading —
 * a page of claims with no dates is a marketing page.
 */

import { useEffect, useState } from 'react';
import { api, errorMessage, type TrustPage } from '../api';
import { useTheme } from '../theme';
import { Alert, Button, Dialog, Field, Input, Spinner, formatDate } from '../ui';
import {
  DownloadIcon,
  FileTextIcon,
  Icon,
  IconHover,
  LockIcon,
  MoonIcon,
  SunIcon,
  Wordmark,
} from '../icons';

type PageDocument = TrustPage['documents'][number];

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The gate.
 *
 * Deliberately a real form rather than a checkbox: the value to the vendor is
 * knowing who asked, and the value to the reader is that the terms are on screen
 * at the moment they agree to them, not linked from a footer.
 */
function AccessDialog({
  slug,
  document,
  terms,
  companyName,
  onClose,
}: {
  slug: string;
  document: PageDocument;
  terms: string;
  companyName: string;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const needsAcceptance = terms.trim() !== '';

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const { token } = await api.requestDocumentAccess(slug, document.id, {
        name,
        email,
        company,
        accepted: needsAcceptance ? accepted : true,
      });
      // Navigating rather than fetching: the response is an attachment, and the
      // browser's own download handling is better than anything rebuilt here.
      window.location.href = api.documentDownloadUrl(slug, document.id, token);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={document.title}
      description={`Requested from ${companyName}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            icon={DownloadIcon}
            busy={busy}
            disabled={
              !name.trim() || !email.trim() || !company.trim() || (needsAcceptance && !accepted)
            }
            onClick={submit}
          >
            Accept and download
          </Button>
        </>
      }
    >
      {error ? (
        <div className="mb-4">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Your name">
            <Input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
          </Field>
          <Field label="Work email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </Field>
        </div>
        <Field label="Company">
          <Input
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            autoComplete="organization"
          />
        </Field>

        {needsAcceptance ? (
          <div>
            <p className="label">Terms</p>
            <div className="prose-body max-h-48 overflow-y-auto rounded-lg border border-line bg-sunken p-3 text-[13px] text-muted">
              {terms}
            </div>
            <label className="mt-3 flex cursor-pointer items-start gap-2.5 text-[13px]">
              <input
                type="checkbox"
                className="mt-1 accent-[var(--vk-accent)]"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
              />
              <span>I have read and accept these terms on behalf of my company.</span>
            </label>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

export function TrustView({ slug }: { slug: string }) {
  const [page, setPage] = useState<TrustPage | null>(null);
  const [missing, setMissing] = useState(false);
  const [requesting, setRequesting] = useState<PageDocument | null>(null);
  const { isDark, setTheme } = useTheme();

  useEffect(() => {
    void api
      .trust(slug)
      .then(setPage)
      .catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-5 text-center">
        <h1 className="text-[20px]">No trust page here</h1>
        <p className="mt-2 text-[13.5px] text-muted">
          This address does not point at a published page.
        </p>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size={20} />
      </div>
    );
  }

  // Defensive: this page is read by strangers evaluating the company, and a
  // white screen because one field was absent is the worst failure it has.
  const documents = page.documents ?? [];
  const terms = page.terms ?? '';

  return (
    <div className="min-h-screen">
      <header className="border-b border-line">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <span className="text-[13px] text-muted">Security overview</span>
          <IconHover
            as="button"
            className="btn btn-ghost"
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
            aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            <Icon icon={isDark ? SunIcon : MoonIcon} size={16} />
          </IconHover>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-12">
        <h1 className="text-[30px]">{page.companyName}</h1>
        {page.intro ? (
          <p className="prose-body mt-4 max-w-2xl text-[15px] text-muted">{page.intro}</p>
        ) : null}

        <div className="mt-10 space-y-4">
          {page.evidence.length === 0 ? (
            <p className="text-[13.5px] text-faint">
              Nothing has been published here yet.
            </p>
          ) : (
            page.evidence.map((card) => (
              <article key={card.id} className="card p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <h2 className="text-[16px] font-medium">{card.title}</h2>
                  <span className="chip chip-neutral">
                    Reviewed {formatDate(card.reviewedAt)}
                  </span>
                </div>
                <p className="mt-1 text-[13px] text-faint">{card.source}</p>
                <p className="prose-body mt-3 text-[14px] text-muted">{card.body}</p>
              </article>
            ))
          )}
        </div>

        {documents.length > 0 ? (
          <section className="mt-12">
            <h2 className="text-[18px]">Documents</h2>
            <p className="mt-1.5 text-[13.5px] text-muted">
              Available after you identify yourself
              {terms.trim() !== '' ? ' and accept the terms' : ''}.
            </p>

            <div className="mt-5 space-y-2.5">
              {documents.map((doc) => (
                <IconHover
                  key={doc.id}
                  as="button"
                  className="card flex w-full items-center gap-3 p-4 text-left"
                  onClick={() => setRequesting(doc)}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sunken text-muted">
                    <Icon icon={FileTextIcon} size={17} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] font-medium">{doc.title}</span>
                    {doc.description ? (
                      <span className="mt-0.5 block text-[13px] text-muted">{doc.description}</span>
                    ) : null}
                    <span className="mt-0.5 block text-[12px] text-faint">
                      {doc.filename} · {formatSize(doc.sizeBytes)}
                    </span>
                  </span>
                  <span className="chip chip-neutral shrink-0">
                    <Icon icon={LockIcon} size={12} isAnimated={false} />
                    Request
                  </span>
                </IconHover>
              ))}
            </div>
          </section>
        ) : null}

        <p className="mt-10 text-[12.5px] leading-relaxed text-faint">
          Each statement above is dated with the last time a person at {page.companyName} reviewed
          it. Anything past its review window is withheld from this page rather than shown with a
          caveat.
        </p>
      </main>

      {requesting ? (
        <AccessDialog
          slug={slug}
          document={requesting}
          terms={terms}
          companyName={page.companyName}
          onClose={() => setRequesting(null)}
        />
      ) : null}

      <footer className="border-t border-line py-6">
        <div className="mx-auto flex max-w-3xl items-center justify-center gap-1.5 px-5 text-[12px] text-faint">
          <Wordmark size={13} />
          Published with Vouchkeep
        </div>
      </footer>
    </div>
  );
}
